// Shared building blocks for the Worker page sections (E-3 L3 M3).
import { useState, type ReactNode } from 'react';
import { Badge } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import { EmptyState } from '@heyroot/ui/primitives/EmptyState';
import { ErrorState } from '@heyroot/ui/primitives/ErrorState';
import { Input } from '@heyroot/ui/primitives/Input';
import { SkeletonTable } from '@heyroot/ui/primitives/Skeleton';
import { useAdminAuth } from '../../lib/auth-context';
import { PERMS } from '../../../rbac/permissions';

/** Action buttons render only for WORKER_OPERATE (P2-H03); the server enforces it too. */
export function useCanOperate(): boolean {
  return useAdminAuth().hasPerm(PERMS.WORKER_OPERATE);
}

export function fmtTs(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/** A pause's end. 'infinity' (a boot pause; L0b sync writes it) has no end time. */
export function fmtPauseEnd(until: string | null | undefined): string {
  return until === 'infinity' ? 'indefinitely' : `until ${fmtTs(until)}`;
}

export function fmtAge(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86_400)}d`;
}

export function SeverityBadge({ severity }: { severity: string }) {
  return <Badge variant={severity === 'page' ? 'danger' : 'warning'}>{severity}</Badge>;
}

/**
 * The pause vocabulary (IF-55 (d)): 'boot-paused', 'breaker', 'operator:<reason>'.
 * A pause counts only while paused_until is in the future (SQ-21).
 */
export function PauseBadge({ pausedNow, reason }: { pausedNow: boolean; reason: string | null }) {
  if (!pausedNow) return null;
  const label = reason ?? 'paused';
  return (
    <Badge variant={label === 'breaker' ? 'danger' : 'warning'} title={label}>
      {label.length > 60 ? `${label.slice(0, 59)}…` : label}
    </Badge>
  );
}

export function SectionHeading({ title, description }: { title: string; description: string }) {
  return (
    <div>
      <h2 className="text-heading-3 text-primary">{title}</h2>
      <p className="mt-1 text-body-sm text-muted">{description}</p>
    </div>
  );
}

/** Loading, error and empty states shared by every section; renders children otherwise. */
export function QueryState({
  isPending,
  isError,
  isEmpty,
  emptyTitle,
  onRetry,
  children,
}: {
  isPending: boolean;
  isError: boolean;
  isEmpty: boolean;
  emptyTitle: string;
  onRetry: () => void;
  children: ReactNode;
}) {
  if (isPending) return <SkeletonTable rows={4} cols={5} />;
  if (isError)
    return <ErrorState description="Worker data could not be loaded." onRetry={onRetry} />;
  if (isEmpty) return <EmptyState variant="inline" title={emptyTitle} />;
  return <>{children}</>;
}

/** The outcome of the last action: the server's own refusal text (e.g. unsupported_tz). */
export function ActionOutcome({ error, success }: { error: unknown; success: string | null }) {
  if (error) {
    const message = error instanceof Error ? error.message : 'The action failed.';
    return (
      <p role="alert" className="text-body-sm text-danger-text">
        {message}
      </p>
    );
  }
  if (success) {
    return (
      <p role="status" className="text-body-sm text-muted">
        {success}
      </p>
    );
  }
  return null;
}

const PAUSE_PRESETS: ReadonlyArray<{ label: string; ms: number }> = [
  { label: '1h', ms: 60 * 60 * 1000 },
  { label: '24h', ms: 24 * 60 * 60 * 1000 },
  { label: '7d', ms: 7 * 24 * 60 * 60 * 1000 },
];

/**
 * Inline pause form: a required reason (stored as `operator:<reason>`) and a
 * preset duration. An operator pause always has an end; unpause clears it.
 */
export function PauseForm({
  target,
  onPause,
  onCancel,
  busy,
}: {
  target: string;
  onPause: (untilIso: string, reason: string) => void;
  onCancel: () => void;
  busy: boolean;
}) {
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();
  return (
    <div className="flex items-center gap-2 flex-wrap" role="group" aria-label={`Pause ${target}`}>
      <div className="basis-56">
        <Input
          type="text"
          size="sm"
          value={reason}
          maxLength={200}
          placeholder="Reason (required)"
          aria-label={`Pause reason for ${target}`}
          onChange={(e) => setReason(e.target.value)}
        />
      </div>
      {PAUSE_PRESETS.map((p) => (
        <Button
          key={p.label}
          size="sm"
          variant="secondary"
          disabled={!trimmed || busy}
          onClick={() => onPause(new Date(Date.now() + p.ms).toISOString(), trimmed)}
        >
          Pause {p.label}
        </Button>
      ))}
      <Button size="sm" variant="ghost" onClick={onCancel}>
        Cancel
      </Button>
    </div>
  );
}
